import React from 'react';

const DEFAULT_MAX_LABELS = 8;
const DEFAULT_DENSE_THRESHOLD = 12;
const RADIAN = Math.PI / 180;

const formatDefaultValue = (value: number | string) => {
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1000) {
      return `${Math.round(value).toLocaleString()}`;
    }
    return value % 1 === 0 ? `${value}` : value.toFixed(1);
  }
  return value;
};

interface LabelDensityOptions {
  containerWidth?: number;
  minLabelSpacing?: number;
  denseThreshold?: number;
}

const DEFAULT_LABEL_SPACING = 48;

export const getLabelStep = (
  length: number,
  maxLabels: number = DEFAULT_MAX_LABELS,
  options: LabelDensityOptions = {}
) => {
  const { containerWidth, minLabelSpacing = DEFAULT_LABEL_SPACING, denseThreshold = DEFAULT_DENSE_THRESHOLD } = options;
  const safeLength = Math.max(length, 1);

  if (safeLength <= denseThreshold) {
    return 1;
  }

  if (containerWidth && minLabelSpacing > 0) {
    const capacity = Math.max(Math.floor(containerWidth / minLabelSpacing), 1);
    if (capacity >= safeLength) {
      return 1;
    }
    const adjustedMax = Math.max(Math.min(capacity, safeLength), 1);
    if (adjustedMax >= safeLength) {
      return 1;
    }
    return Math.ceil(safeLength / Math.max(adjustedMax, maxLabels));
  }

  if (safeLength <= maxLabels) {
    return 1;
  }

  return Math.ceil(safeLength / maxLabels);
};

interface BarLabelOptions {
  dataLength: number;
  formatValue?: (value: number, index?: number) => string;
  stacked?: boolean;
  orientation?: 'vertical' | 'horizontal';
  maxLabels?: number;
  minValue?: number;
  containerWidth?: number;
  minLabelSpacing?: number;
  denseThreshold?: number;
  minBarWidthForLabel?: number;
  minBarHeightForLabel?: number;
  minInsideBarWidthForLabel?: number;
  fontSize?: number;
}

export const createBarLabelRenderer = ({
  dataLength,
  formatValue,
  stacked = false,
  orientation = 'vertical',
  maxLabels = DEFAULT_MAX_LABELS,
  minValue = 0,
  containerWidth,
  minLabelSpacing,
  denseThreshold,
  minBarWidthForLabel = 0,
  minBarHeightForLabel = 0,
  minInsideBarWidthForLabel = 56,
  fontSize = 10,
}: BarLabelOptions) => {
  const step = getLabelStep(dataLength, maxLabels, { containerWidth, minLabelSpacing, denseThreshold });

  return (props: any) => {
    const { x = 0, y = 0, width = 0, height = 0, value, index = 0 } = props;
    if (value === null || value === undefined) {
      return null;
    }
    if (Math.abs(Number(value)) <= minValue) {
      return null;
    }
    const barWidth = Math.abs(width);
    const barHeight = Math.abs(height);
    const forceOutside =
      (minBarWidthForLabel > 0 && barWidth < minBarWidthForLabel) ||
      (minBarHeightForLabel > 0 && barHeight < minBarHeightForLabel);
    const deEmphasizeDenseLabel = step > 1 && index % step !== 0;

    const labelValue = formatValue ? formatValue(Number(value), index) : formatDefaultValue(value);
    if (orientation === 'horizontal') {
      const isWideBar = !forceOutside && barWidth >= minInsideBarWidthForLabel;
      const textX = isWideBar ? x + width - 8 : x + width + 6;
      const textAnchor = isWideBar ? 'end' : 'start';
      const isOutside = !isWideBar;
      return (
        <text
          x={textX}
          y={y + height / 2}
          fill={isWideBar ? '#ffffff' : '#14385d'}
          stroke={isOutside ? '#f8fafc' : undefined}
          strokeWidth={isOutside ? 3 : undefined}
          paintOrder={isOutside ? 'stroke' : undefined}
          opacity={deEmphasizeDenseLabel ? 0.72 : 1}
          fontSize={fontSize}
          fontWeight={600}
          textAnchor={textAnchor}
          dominantBaseline="middle"
        >
          {labelValue}
        </text>
      );
    }

    const centerX = x + width / 2;
    const isNearTop = y < 20;
    const isTallBar = barHeight > 40;
    const shouldPlaceInside = !forceOutside && (stacked || isNearTop || isTallBar);
    let centerY: number;
    if (shouldPlaceInside && stacked) {
      centerY = y + height / 2;
    } else if (shouldPlaceInside) {
      centerY = y + Math.max(barHeight / 2, 12);
    } else {
      centerY = Math.max(fontSize + 2, y - 6);
    }

    return (
      <text
        x={centerX}
        y={centerY}
        fill={shouldPlaceInside ? '#ffffff' : '#14385d'}
        stroke={shouldPlaceInside ? undefined : '#f8fafc'}
        strokeWidth={shouldPlaceInside ? undefined : 3}
        paintOrder={shouldPlaceInside ? undefined : 'stroke'}
        opacity={deEmphasizeDenseLabel ? 0.72 : 1}
        fontSize={fontSize}
        fontWeight={600}
        textAnchor="middle"
      >
        {labelValue}
      </text>
    );
  };
};

interface PieLabelOptions {
  dataLength: number;
  formatValue?: (value: number, payload?: any, percent?: number) => string;
  maxLabels?: number;
  minPercentage?: number;
  insideThreshold?: number;
  containerWidth?: number;
  minLabelSpacing?: number;
  denseThreshold?: number;
}

const shouldRenderPieValue = (
  percent: number,
  index: number,
  step: number,
  minPercentage: number
) => {
  if (!Number.isFinite(percent) || percent <= 0) {
    return false;
  }
  if (percent < minPercentage) {
    return false;
  }
  if (step > 1 && index % step !== 0) {
    return false;
  }
  return true;
};

export const createPieLabelRenderer = ({
  dataLength,
  formatValue,
  maxLabels = 6,
  minPercentage = 0.03,
  insideThreshold = 0.1,
  containerWidth,
  minLabelSpacing,
  denseThreshold,
}: PieLabelOptions) => {
  const step = getLabelStep(dataLength, maxLabels, { containerWidth, minLabelSpacing, denseThreshold });

  return (props: any) => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      innerRadius = 0,
      outerRadius = 0,
      percent = 0,
      value,
      index = 0,
      payload,
    } = props;

    if (value === null || value === undefined) {
      return null;
    }
    if (!shouldRenderPieValue(percent, index, step, minPercentage)) {
      return null;
    }

    const inside = percent >= insideThreshold;
    const radius = inside
      ? innerRadius + (outerRadius - innerRadius) * 0.5
      : outerRadius + 14;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const labelValue = formatValue
      ? formatValue(Number(value), payload, percent)
      : `${Math.round(percent * 100)}%`;

    return (
      <text
        x={x}
        y={y}
        fill={inside ? '#ffffff' : '#263238'}
        fontSize={11}
        fontWeight={600}
        textAnchor={inside ? 'middle' : (x >= cx ? 'start' : 'end')}
        dominantBaseline="middle"
      >
        {labelValue}
      </text>
    );
  };
};

export const createPieLabelLineRenderer = ({
  dataLength,
  maxLabels = 6,
  minPercentage = 0.03,
  insideThreshold = 0.1,
  containerWidth,
  minLabelSpacing,
  denseThreshold,
}: PieLabelOptions) => {
  const step = getLabelStep(dataLength, maxLabels, { containerWidth, minLabelSpacing, denseThreshold });

  return (props: any) => {
    const {
      percent = 0,
      index = 0,
      points = [],
      stroke = '#7b8ca3',
    } = props;

    if (!shouldRenderPieValue(percent, index, step, minPercentage)) {
      return null;
    }

    // Inside labels don't need connector lines.
    if (percent >= insideThreshold) {
      return null;
    }

    if (!Array.isArray(points) || points.length < 2) {
      return null;
    }

    const first = points[0];
    const second = points[1];
    const third = points.length > 2 ? points[2] : null;
    const path = third
      ? `M${first.x},${first.y}L${second.x},${second.y}L${third.x},${third.y}`
      : `M${first.x},${first.y}L${second.x},${second.y}`;

    return (
      <path
        d={path}
        stroke={stroke}
        strokeWidth={1.5}
        fill="none"
      />
    );
  };
};

interface LineLabelOptions {
  dataLength: number;
  formatValue?: (value: number, index?: number) => string;
  maxLabels?: number;
  minValue?: number;
  containerWidth?: number;
  minLabelSpacing?: number;
  denseThreshold?: number;
  offsetY?: number;
  fontSize?: number;
}

export const createLineLabelRenderer = ({
  dataLength,
  formatValue,
  maxLabels = DEFAULT_MAX_LABELS,
  minValue = 0,
  containerWidth,
  minLabelSpacing,
  denseThreshold,
  offsetY = 12,
  fontSize = 11,
}: LineLabelOptions) => {
  const step = getLabelStep(dataLength, maxLabels, { containerWidth, minLabelSpacing, denseThreshold });

  return (props: any) => {
    const { x = 0, y = 0, value, index = 0 } = props;
    if (value === null || value === undefined) {
      return null;
    }
    if (Math.abs(Number(value)) <= minValue) {
      return null;
    }
    if (step > 1 && index % step !== 0) {
      return null;
    }

    const labelValue = formatValue ? formatValue(Number(value), index) : formatDefaultValue(value);

    return (
      <text
        x={x}
        y={y - offsetY}
        fill="#37474F"
        fontSize={fontSize}
        fontWeight={600}
        textAnchor="middle"
      >
        {labelValue}
      </text>
    );
  };
};
